<template>
  <CCardBody>

    <!-- HEADER -->
    <div class="d-flex align-items-center justify-content-between mb-3">
      <!-- SEARCH -->
      <div class="d-flex align-items-center gap-2">
        <b-form-input
          v-model="search"
          size="sm"
          placeholder="Search products..."
          style="width: 220px"
          @keyup.enter="searchProduct(1)"
        />

        <b-button
          size="sm"
          variant="primary"
          @click="searchProduct(1)"
        >
          Search
        </b-button>

        <b-button
          size="sm"
          variant="outline-secondary"
          v-if="search"
          @click="clearSearch"
        >
          Clear
        </b-button>
      </div>

      <b-button
        variant="success"
        class="ml-2"
        @click="openFactoryModal"
      >
        Set Default Products
      </b-button>
    </div>

    <!-- PRODUCTS TABLE -->
    <b-table
      hover
      responsive
      show-empty
      :items="products"
      :fields="auth_role?.name !== 'admin' ? fields : merchant_fields"
    >
      <template #cell(index)="item">
        {{ item.index + 1 }}
      </template>

      <template #cell(sku_number)="item">
        {{ item.item.sku?.sku_number || '—' }}
      </template>

      <template #cell(category)="item">
        {{ item.item.categories.map(c => c.category_name).join(', ') }}
      </template>

      <!-- SHAREABLE COLUMN -->
      <template
        #cell(shareable)="item"
        v-if="$can('product-listing-shareable-column')"
      >
        {{ item.item.shareable_info_obj?.factory?.name || '—' }}
      </template>

      <!-- ACTIONS COLUMN -->
      <template #cell(actions)="item">
        <Pencil class="edit_role"  @click="goToProductDetail(item.item.id)" fillColor="#009FE3"/>
      </template>

      <template #empty>
        <div class="text-center text-muted py-4">
          No data exists
        </div>
      </template>
    </b-table>

    <!-- PAGINATION -->
    <div class="d-flex justify-content-center mt-3">
      <b-pagination
        v-if="pagination.total > 0"
        v-model="pagination.currentPage"
        :total-rows="pagination.rows"
        :per-page="pagination.perPage"
        @change="handlePagination"
      />
    </div>

    <!-- FACTORY MODAL -->
    <b-modal
      id="factories-modal"
      :key="modalKey"
      title="Select Default Products"
      size="lg"
      centered
      scrollable
      hide-header-close
      body-class="factory-modal-body"
      footer-class="factory-modal-footer"
    >
      <!-- MODAL BODY -->
      <div
        v-for="(factory, fIndex) in factories"
        :key="`factory-${fIndex}`"
        class="factory-block"
      >
        <div
          class="factory-header clickable"
          v-b-toggle="`collapse-${fIndex}`"
        >
          <!-- Checkbox (prevent toggle when clicking checkbox) -->
          <b-form-checkbox
            v-model="factory.selected"
            @click.stop
            @change="toggleAllProducts(factory, factory.selected ? 'select' : 'deselect')"
          >
            <strong>{{ factory.name }}</strong>
            <small class="text-muted ml-1">
              ({{ factory.selected_products_count }}/{{ factory.total_products }})
            </small>
          </b-form-checkbox>

          <!-- Icon (purely visual now) -->
          <b-icon-chevron-down />
        </div>
        <b-collapse :id="`collapse-${fIndex}`" class="mt-2">
          <div class="d-flex gap-2 mb-2">
            <b-button
              size="sm"
              variant="outline-success"
              @click="toggleAllProducts(factory, 'select')"
            >
              Select all
            </b-button>
            <b-button
              size="sm"
              variant="outline-danger"
              @click="toggleAllProducts(factory, 'deselect')"
            >
              Deselect all
            </b-button>
          </div>

          <div class="product-list">
            <b-form-checkbox
              v-for="(product, pIndex) in factory.products"
              :key="`product-${pIndex}`"
              v-model="product.selected"
              @change="handleProductSelection(factory)"
            >
              {{ product.display_name }}
            </b-form-checkbox>
          </div>
        </b-collapse>
      </div>

      <!-- MODAL FOOTER -->
      <template #modal-footer>
        <div class="w-100 d-flex justify-content-between">
          <b-button
            variant="secondary"
            @click="$bvModal.hide('factories-modal')"
          >
            Cancel
          </b-button>
          <b-button
            variant="success"
            @click="saveDefaultProducts"
          >
            Update Default Products
          </b-button>
        </div>
      </template>
    </b-modal>

  </CCardBody>
</template>


<script>
  import Pencil from 'vue-material-design-icons/GreasePencil.vue';
import { CCardBody } from "@coreui/vue";
import {
  BIconChevronDown,
  
} from "bootstrap-vue";
import { http } from "@/config/httpCommon";
import { setPageModal, getPageModal } from "@/helpers/Helpers";

const fields = [
  { key: "index", label: "Sr No" },
  { key: "product_name", label: "Product Name" },
  { key: "sku_number", label: "SKU No." },
  { key: "category", label: "Category" },
  { key: "shareable", label: "Shareable" },
  { key: "actions", label: "Actions" }
];

export default {
  name: "Products",

  components: {
    CCardBody,
    BIconChevronDown,
    Pencil
  },

  data() {
    return {
      products: [],
      factories: [],
      modalKey: 0,
      auth_role: {},
      search: String(""),

      pagination: {
        rows: 0,
        currentPage: 1,
        perPage: 10,
        total: 0
      },

      fields,
      merchant_fields: fields
    };
  },

  async created() {
    this.auth_role = this.$store.getters.userRole;

    const modal = getPageModal();
    if (modal?.search) {
      this.search = typeof modal.search === "string"
        ? modal.search
        : "";

      this.searchProduct(modal.page || 1);
    }else{
      this.searchProduct();
    }
  },

  methods: {
    /* PRODUCTS */
    async getProducts(params) {
      const res = await http.get("products" + params);
      this.products = res.data.data.data;
      this.makePagination(res.data.data);
    },

    makePagination(data) {
      this.pagination.currentPage = data.current_page;
      this.pagination.rows = data.total;
      this.pagination.perPage = data.per_page;
      this.pagination.total = data.total;
    },

    searchProduct(page = 1) {
      let params = `?is_default_only=1&page=${page}`;
      if (this.search) params += `&search=${this.search}`;
      setPageModal(page, this.$route.name, this.search);
      this.getProducts(params);
    },

    clearSearch() {
      this.search = "";
      this.searchProduct(1);
    },

    handlePagination(page) {
      this.searchProduct(page);
    },

    /* ACTIONS */
    goToProductDetail(productId) {
      this.$router.push('/products/update-default-product/'+productId);
    },

    /* FACTORIES */
    async getFactories() {
      const res = await this.$store.dispatch(
        "getAllFactories",
        "?with_products=1"
      );
      this.factories = JSON.parse(JSON.stringify(res.data));
    },

    async openFactoryModal() {
      this.factories = [];
      this.modalKey++;
      await this.getFactories();
      this.$nextTick(() => {
        this.$bvModal.show("factories-modal");
      });
    },

    toggleAllProducts(factory, action) {
      const value = action === "select";
      factory.products.forEach(p => (p.selected = value));
      factory.selected = value;
      factory.selected_products_count = value
        ? factory.total_products
        : 0;
    },

    handleProductSelection(factory) {
      const count = factory.products.filter(p => p.selected).length;
      factory.selected_products_count = count;
      factory.selected = count === factory.total_products;
    },

    async saveDefaultProducts() {
      const ids = [];

      this.factories.forEach(factory => {
        factory.products.forEach(product => {
          if (product.selected) ids.push(product.id);
        });
      });

      await http.post("mark/products/default", {
        product_ids: ids
      });

      this.$bvModal.hide("factories-modal");
      await this.getProducts("?is_default_only=1");

      this.showToast(
        "Default products updated successfully",
        "SUCCESS"
      );
    }
  }
};
</script>


<style scoped>
.factory-modal-body {
  max-height: 65vh;
  overflow-y: auto;
}

.factory-modal-footer {
  border-top: 1px solid #e9ecef;
  padding-top: 12px;
}

.factory-block {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
}

.factory-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.product-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 6px;
}

.factory-header.clickable {
  cursor: pointer;
}


</style>
